// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConfirmationModal,
  ToastContainer,
  LoadingScreen,
  EmptyState,
  SkeletonCard,
  SkeletonList,
  ProgressBar,
  CircularProgress,
  StepProgress,
  Alert,
  Button,
  Badge,
  Card,
  CardHeader,
  CardFooter,
  Input,
} from '@azyntor-labs/nexus-ui';
import type { ToastType, ToastVariant, ToastSize, ToastPosition } from '@azyntor-labs/nexus-ui';
import {
  Sparkles,
  Trash2,
  AlertCircle,
  CheckCircle,
  Info,
  Download,
  Upload,
  Mail,
  Bell,
  Star,
  Zap,
  Inbox,
  FileQuestion,
  Brain,
  Database,
  Code,
  MousePointer,
  CreditCard,
  User,
  Search,
  Eye,
  EyeOff,
  Layers,
  Package
} from 'lucide-react';

interface ToastItem {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  variant?: ToastVariant;
  size?: ToastSize;
  icon?: React.ComponentType<{ className?: string }>;
  actionLabel?: string;
  onAction?: () => void;
  show3D?: boolean;
  showProgress?: boolean;
}

export const ComponentShowcase = () => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [loadingVariant, setLoadingVariant] = useState<'helix' | 'cyber' | 'education' | 'neural' | 'molecular' | 'quantum' | 'database' | 'matrix'>('helix');
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [toastPosition, setToastPosition] = useState<ToastPosition>('top-right');

  const addToast = (
    type: ToastType,
    title: string,
    message?: string,
    options?: Partial<ToastItem>
  ) => {
    const id = Date.now().toString() + Math.random();
    setToasts((prev) => [
      ...prev,
      {
        id,
        type,
        title,
        message,
        duration: 7000,
        variant: 'glass',
        size: 'md',
        show3D: true,
        showProgress: true,
        ...options
      }
    ]);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const handleModalConfirm = async () => {
    setModalLoading(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setModalLoading(false);
    setShowConfirmModal(false);
    setShowDeleteModal(false);
    setShowWarningModal(false);
    setShowSuccessModal(false);
    addToast('success', 'Action completed!', 'Your request was processed successfully');
  };

  const handleShowLoading = (variant: 'helix' | 'cyber' | 'education' | 'neural' | 'molecular' | 'quantum' | 'database' | 'matrix') => {
    setLoadingVariant(variant);
    setShowLoading(true);
    setTimeout(() => setShowLoading(false), 4000);
  };

  return (
    <div className="min-h-screen bg-[#0d0d12] p-4 sm:p-6 lg:p-8">
      {/* Toast Container */}
      <ToastContainer toasts={toasts} onClose={removeToast} position={toastPosition} maxToasts={5} />

      {/* Loading Screen Overlay */}
      {showLoading && (
        <div className="fixed inset-0 z-50">
          <LoadingScreen
            message={
              loadingVariant === 'helix' ? 'DNA Analysis in Progress...' :
              loadingVariant === 'cyber' ? 'Security Scan Running...' :
              loadingVariant === 'education' ? 'Loading Course Materials...' :
              loadingVariant === 'neural' ? 'Training Neural Network...' :
              loadingVariant === 'molecular' ? 'Analyzing Molecular Structure...' :
              loadingVariant === 'quantum' ? 'Quantum Processing Active...' :
              loadingVariant === 'database' ? 'Syncing Database Layers...' :
              loadingVariant === 'matrix' ? 'Matrix Code Streaming...' :
              'Processing...'
            }
            submessage={
              loadingVariant === 'helix' ? 'Analyzing genetic patterns with quantum algorithms' :
              loadingVariant === 'cyber' ? 'Scanning for threats and vulnerabilities across 127 nodes' :
              loadingVariant === 'education' ? 'Preparing interactive learning modules and assessments' :
              loadingVariant === 'neural' ? 'Processing 10,000 data points through deep learning layers' :
              loadingVariant === 'molecular' ? 'Computing molecular bonds and chemical interactions' :
              loadingVariant === 'quantum' ? 'Superposition calculations in quantum field simulation' :
              loadingVariant === 'database' ? 'Synchronizing 5 data layers across distributed clusters' :
              loadingVariant === 'matrix' ? 'Decoding binary streams through cyberspace corridors' :
              'Please wait while we process your request'
            }
            variant={loadingVariant}
            fullScreen={true}
          />
        </div>
      )}

      {/* Modals */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleModalConfirm}
        title="Publish Exam?"
        message="Once published, students will be able to see and take this exam. Are you sure you want to continue?"
        confirmText="Publish"
        type="info"
        isLoading={modalLoading}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleModalConfirm}
        title="Delete Advanced Calculus Final Exam"
        message="Are you sure you want to delete this exam? This action cannot be undone."
        variant="danger"
        isLoading={modalLoading}
      />

      <ConfirmationModal
        isOpen={showWarningModal}
        onClose={() => setShowWarningModal(false)}
        onConfirm={handleModalConfirm}
        title="Warning: Action Required"
        message="This action will affect multiple students. Make sure you want to proceed with this operation."
        confirmText="Proceed"
        type="warning"
        isLoading={modalLoading}
      />

      <ConfirmationModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onConfirm={handleModalConfirm}
        title="Exam Graded Successfully"
        message="All student submissions have been graded and results are ready to be published."
        confirmText="Publish Results"
        type="success"
        isLoading={modalLoading}
      />

      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-4 opacity-0 animate-fadeIn">
          <div className="flex items-center justify-center gap-3">
            <Sparkles className="w-8 h-8 text-teal-400 animate-pulse" />
            <h1 className="text-4xl sm:text-5xl font-black text-white">UI Component Showcase</h1>
            <Sparkles className="w-8 h-8 text-cyan-400 animate-pulse" style={{ animationDelay: '0.5s' }} />
          </div>
          <p className="text-gray-400 text-lg">
            Modern, animated components with Anarisk dark theme & Three.js
          </p>
          <div className="h-1 w-48 bg-gradient-to-r from-teal-500 via-cyan-500 to-purple-500 rounded-full mx-auto shadow-lg shadow-teal-500/30 animate-pulse-slow" />
        </div>

        {/* Toast Position Selector */}
        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-6 hover:border-white/[0.12] transition-all opacity-0 animate-fadeIn" style={{ animationDelay: '100ms' }}>
          <h2 className="text-xl font-bold text-white mb-4">Toast Position</h2>
          <div className="grid grid-cols-3 gap-2">
            {(['top-left', 'top-center', 'top-right', 'bottom-left', 'bottom-center', 'bottom-right'] as ToastPosition[]).map((pos) => (
              <button
                key={pos}
                onClick={() => setToastPosition(pos)}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                  toastPosition === pos
                    ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30'
                    : 'bg-white/[0.04] text-gray-400 border border-white/[0.06] hover:bg-white/[0.08]'
                }`}
              >
                {pos.replace('-', ' ').toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Toast Variants Section */}
        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-6 hover:border-white/[0.12] transition-all opacity-0 animate-fadeIn" style={{ animationDelay: '200ms' }}>
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-400" />
            Toast Variants (7s duration, 3D enabled)
          </h2>
          <p className="text-gray-400 text-sm mb-6">
            6 variants × 4 sizes × 6 positions = 144 configurations!
          </p>

          {/* Variants Grid */}
          <div className="space-y-6">
            {/* Glass Variant */}
            <div>
              <h3 className="text-teal-400 font-bold mb-3 text-sm">Glass Variant (with 3D particles)</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <button
                  onClick={() => addToast('success', 'Exam Created!', 'Students can now see your exam', { variant: 'glass' })}
                  className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-xl hover:bg-emerald-500/10 hover:scale-105 transition-all"
                >
                  <CheckCircle className="w-6 h-6 text-emerald-400 mx-auto mb-1.5" />
                  <p className="text-emerald-400 font-semibold text-xs">Success</p>
                </button>

                <button
                  onClick={() => addToast('error', 'Upload Failed', 'Please check your file and try again', { variant: 'glass' })}
                  className="p-3 bg-red-500/5 border border-red-500/20 rounded-xl hover:bg-red-500/10 hover:scale-105 transition-all"
                >
                  <AlertCircle className="w-6 h-6 text-red-400 mx-auto mb-1.5" />
                  <p className="text-red-400 font-semibold text-xs">Error</p>
                </button>

                <button
                  onClick={() => addToast('warning', 'Deadline Approaching', '5 students have not submitted yet', { variant: 'glass' })}
                  className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-xl hover:bg-amber-500/10 hover:scale-105 transition-all"
                >
                  <AlertCircle className="w-6 h-6 text-amber-400 mx-auto mb-1.5" />
                  <p className="text-amber-400 font-semibold text-xs">Warning</p>
                </button>

                <button
                  onClick={() => addToast('info', 'System Update', 'New features available in settings', { variant: 'glass' })}
                  className="p-3 bg-blue-500/5 border border-blue-500/20 rounded-xl hover:bg-blue-500/10 hover:scale-105 transition-all"
                >
                  <Info className="w-6 h-6 text-blue-400 mx-auto mb-1.5" />
                  <p className="text-blue-400 font-semibold text-xs">Info</p>
                </button>
              </div>
            </div>

            {/* Gradient Variant */}
            <div>
              <h3 className="text-cyan-400 font-bold mb-3 text-sm">Gradient Variant</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <button
                  onClick={() => addToast('success', 'Grade Submitted', 'All students have been notified', { variant: 'gradient', size: 'lg' })}
                  className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-xl hover:bg-emerald-500/10 hover:scale-105 transition-all"
                >
                  <p className="text-emerald-400 font-semibold text-xs">Large Success</p>
                </button>

                <button
                  onClick={() => addToast('error', 'Connection Lost', 'Unable to reach server', { variant: 'gradient', size: 'lg' })}
                  className="p-3 bg-red-500/5 border border-red-500/20 rounded-xl hover:bg-red-500/10 hover:scale-105 transition-all"
                >
                  <p className="text-red-400 font-semibold text-xs">Large Error</p>
                </button>

                <button
                  onClick={() => addToast('warning', 'Low Storage', 'Only 2GB remaining', { variant: 'gradient', size: 'lg' })}
                  className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-xl hover:bg-amber-500/10 hover:scale-105 transition-all"
                >
                  <p className="text-amber-400 font-semibold text-xs">Large Warning</p>
                </button>

                <button
                  onClick={() => addToast('custom', 'Premium Feature', 'Unlock advanced analytics', { variant: 'gradient', size: 'lg', icon: Star })}
                  className="p-3 bg-purple-500/5 border border-purple-500/20 rounded-xl hover:bg-purple-500/10 hover:scale-105 transition-all"
                >
                  <Star className="w-6 h-6 text-purple-400 mx-auto mb-1.5" />
                  <p className="text-purple-400 font-semibold text-xs">Custom</p>
                </button>
              </div>
            </div>

            {/* Solid Variant */}
            <div>
              <h3 className="text-purple-400 font-bold mb-3 text-sm">Solid Variant</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <button
                  onClick={() => addToast('success', 'Saved!', undefined, { variant: 'solid', size: 'sm', show3D: false })}
                  className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-xl hover:bg-emerald-500/10 hover:scale-105 transition-all"
                >
                  <p className="text-emerald-400 font-semibold text-xs">Small Solid</p>
                </button>

                <button
                  onClick={() => addToast('error', 'Failed!', undefined, { variant: 'solid', size: 'sm', show3D: false })}
                  className="p-3 bg-red-500/5 border border-red-500/20 rounded-xl hover:bg-red-500/10 hover:scale-105 transition-all"
                >
                  <p className="text-red-400 font-semibold text-xs">Small Solid</p>
                </button>

                <button
                  onClick={() => addToast('warning', 'Caution!', undefined, { variant: 'solid', size: 'sm', show3D: false })}
                  className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-xl hover:bg-amber-500/10 hover:scale-105 transition-all"
                >
                  <p className="text-amber-400 font-semibold text-xs">Small Solid</p>
                </button>

                <button
                  onClick={() => addToast('info', 'Note', undefined, { variant: 'solid', size: 'sm', show3D: false })}
                  className="p-3 bg-blue-500/5 border border-blue-500/20 rounded-xl hover:bg-blue-500/10 hover:scale-105 transition-all"
                >
                  <p className="text-blue-400 font-semibold text-xs">Small Solid</p>
                </button>
              </div>
            </div>

            {/* With Actions */}
            <div>
              <h3 className="text-amber-400 font-bold mb-3 text-sm">With Action Buttons</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <button
                  onClick={() => addToast('info', 'Download Ready', 'Your report is ready to download', {
                    variant: 'glass',
                    icon: Download,
                    actionLabel: 'Download',
                    onAction: () => alert('Download started!')
                  })}
                  className="p-3 bg-blue-500/5 border border-blue-500/20 rounded-xl hover:bg-blue-500/10 hover:scale-105 transition-all"
                >
                  <Download className="w-6 h-6 text-blue-400 mx-auto mb-1.5" />
                  <p className="text-blue-400 font-semibold text-xs">Download</p>
                </button>

                <button
                  onClick={() => addToast('success', 'Upload Complete', '25 files uploaded successfully', {
                    variant: 'glass',
                    icon: Upload,
                    actionLabel: 'View Files',
                    onAction: () => alert('Opening files...')
                  })}
                  className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-xl hover:bg-emerald-500/10 hover:scale-105 transition-all"
                >
                  <Upload className="w-6 h-6 text-emerald-400 mx-auto mb-1.5" />
                  <p className="text-emerald-400 font-semibold text-xs">Upload</p>
                </button>

                <button
                  onClick={() => addToast('custom', 'New Message', 'You have 3 unread messages', {
                    variant: 'glass',
                    icon: Mail,
                    actionLabel: 'Read',
                    onAction: () => alert('Opening messages...')
                  })}
                  className="p-3 bg-purple-500/5 border border-purple-500/20 rounded-xl hover:bg-purple-500/10 hover:scale-105 transition-all"
                >
                  <Mail className="w-6 h-6 text-purple-400 mx-auto mb-1.5" />
                  <p className="text-purple-400 font-semibold text-xs">Message</p>
                </button>
              </div>
            </div>

            {/* Different Sizes */}
            <div>
              <h3 className="text-red-400 font-bold mb-3 text-sm">Size Variants</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <button
                  onClick={() => addToast('info', 'Small', 'Compact notification', { size: 'sm', variant: 'bordered' })}
                  className="p-3 bg-blue-500/5 border border-blue-500/20 rounded-xl hover:bg-blue-500/10 hover:scale-105 transition-all"
                >
                  <p className="text-blue-400 font-semibold text-xs">Small (SM)</p>
                </button>

                <button
                  onClick={() => addToast('success', 'Medium', 'Standard notification size', { size: 'md', variant: 'bordered' })}
                  className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-xl hover:bg-emerald-500/10 hover:scale-105 transition-all"
                >
                  <p className="text-emerald-400 font-semibold text-xs">Medium (MD)</p>
                </button>

                <button
                  onClick={() => addToast('warning', 'Large', 'Important notification that needs attention', { size: 'lg', variant: 'bordered' })}
                  className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-xl hover:bg-amber-500/10 hover:scale-105 transition-all"
                >
                  <p className="text-amber-400 font-semibold text-xs">Large (LG)</p>
                </button>

                <button
                  onClick={() => addToast('custom', 'Extra Large', 'Maximum size for critical announcements', { size: 'xl', variant: 'bordered', icon: Zap })}
                  className="p-3 bg-purple-500/5 border border-purple-500/20 rounded-xl hover:bg-purple-500/10 hover:scale-105 transition-all"
                >
                  <p className="text-purple-400 font-semibold text-xs">X-Large (XL)</p>
                </button>
              </div>
            </div>

            {/* Custom Icons */}
            <div>
              <h3 className="text-emerald-400 font-bold mb-3 text-sm">Custom Icons</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
                <button
                  onClick={() => addToast('custom', 'Notification', 'You have a new alert', { icon: Bell, variant: 'glass' })}
                  className="p-3 bg-purple-500/5 border border-purple-500/20 rounded-xl hover:bg-purple-500/10 hover:scale-105 transition-all"
                >
                  <Bell className="w-6 h-6 text-purple-400 mx-auto mb-1.5" />
                  <p className="text-purple-400 font-semibold text-xs">Bell</p>
                </button>

                <button
                  onClick={() => addToast('info', 'Starred', 'Item added to favorites', { icon: Star, variant: 'glass' })}
                  className="p-3 bg-blue-500/5 border border-blue-500/20 rounded-xl hover:bg-blue-500/10 hover:scale-105 transition-all"
                >
                  <Star className="w-6 h-6 text-blue-400 mx-auto mb-1.5" />
                  <p className="text-blue-400 font-semibold text-xs">Star</p>
                </button>

                <button
                  onClick={() => addToast('success', 'Email Sent', 'Notification delivered', { icon: Mail, variant: 'glass' })}
                  className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-xl hover:bg-emerald-500/10 hover:scale-105 transition-all"
                >
                  <Mail className="w-6 h-6 text-emerald-400 mx-auto mb-1.5" />
                  <p className="text-emerald-400 font-semibold text-xs">Mail</p>
                </button>

                <button
                  onClick={() => addToast('warning', 'Deleted', 'Item moved to trash', { icon: Trash2, variant: 'glass' })}
                  className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-xl hover:bg-amber-500/10 hover:scale-105 transition-all"
                >
                  <Trash2 className="w-6 h-6 text-amber-400 mx-auto mb-1.5" />
                  <p className="text-amber-400 font-semibold text-xs">Trash</p>
                </button>

                <button
                  onClick={() => addToast('loading', 'Processing', 'Please wait...', { variant: 'glass', duration: 0 })}
                  className="p-3 bg-teal-500/5 border border-teal-500/20 rounded-xl hover:bg-teal-500/10 hover:scale-105 transition-all"
                >
                  <div className="w-6 h-6 border-2 border-teal-400 border-t-transparent rounded-full animate-spin mx-auto mb-1.5" />
                  <p className="text-teal-400 font-semibold text-xs">Loading</p>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Modals Section - Same as before */}
        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-6 hover:border-white/[0.12] transition-all opacity-0 animate-fadeIn" style={{ animationDelay: '300ms' }}>
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-emerald-400" />
            Confirmation Modals
          </h2>
          <p className="text-gray-400 text-sm mb-6">
            Beautiful modals with floating particles, themed icons, and loading states
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <button
              onClick={() => setShowConfirmModal(true)}
              className="p-4 bg-[#0d0d12] border border-blue-500/20 rounded-xl hover:border-blue-500/40 hover:bg-blue-500/5 hover:scale-105 transition-all"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-3 mx-auto">
                <Info className="w-5 h-5 text-blue-400" />
              </div>
              <h3 className="text-white font-semibold text-sm mb-1">Info Modal</h3>
              <p className="text-gray-500 text-xs">Blue theme</p>
            </button>

            <button
              onClick={() => setShowWarningModal(true)}
              className="p-4 bg-[#0d0d12] border border-amber-500/20 rounded-xl hover:border-amber-500/40 hover:bg-amber-500/5 hover:scale-105 transition-all"
            >
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-3 mx-auto">
                <AlertCircle className="w-5 h-5 text-amber-400" />
              </div>
              <h3 className="text-white font-semibold text-sm mb-1">Warning</h3>
              <p className="text-gray-500 text-xs">Amber theme</p>
            </button>

            <button
              onClick={() => setShowDeleteModal(true)}
              className="p-4 bg-[#0d0d12] border border-red-500/20 rounded-xl hover:border-red-500/40 hover:bg-red-500/5 hover:scale-105 transition-all"
            >
              <div className="w-10 h-10 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-3 mx-auto">
                <Trash2 className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-white font-semibold text-sm mb-1">Delete</h3>
              <p className="text-gray-500 text-xs">Red theme</p>
            </button>

            <button
              onClick={() => setShowSuccessModal(true)}
              className="p-4 bg-[#0d0d12] border border-emerald-500/20 rounded-xl hover:border-emerald-500/40 hover:bg-emerald-500/5 hover:scale-105 transition-all"
            >
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-3 mx-auto">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <h3 className="text-white font-semibold text-sm mb-1">Success</h3>
              <p className="text-gray-500 text-xs">Emerald theme</p>
            </button>
          </div>
        </div>

        {/* Error Pages Section */}
        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-6 hover:border-white/[0.12] transition-all opacity-0 animate-fadeIn" style={{ animationDelay: '400ms' }}>
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <AlertCircle className="w-6 h-6 text-teal-400" />
            Error Pages
          </h2>

          <div className="grid sm:grid-cols-2 gap-4">
            <Link
              to="/random-nonexistent-page"
              className="group p-6 bg-[#0d0d12] border border-white/[0.06] rounded-xl hover:border-teal-500/30 transition-all hover:scale-105"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal-500/20 to-cyan-500/20 border border-teal-500/30 flex items-center justify-center mb-4">
                <span className="text-3xl font-black bg-gradient-to-br from-teal-400 to-cyan-400 bg-clip-text text-transparent">404</span>
              </div>
              <h3 className="text-white font-bold mb-2 text-lg">404 Not Found</h3>
              <p className="text-gray-500 text-xs mb-3 leading-relaxed">
                3D particles, wireframe sphere, orbiting dots, search bar, auto-redirect countdown (10s)
              </p>
              <div className="flex items-center gap-2 text-teal-400 text-sm font-medium">
                View Demo
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </div>
            </Link>

            <Link
              to="/access-denied"
              className="group p-6 bg-[#0d0d12] border border-white/[0.06] rounded-xl hover:border-red-500/30 transition-all hover:scale-105"
            >
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-500/20 to-orange-500/20 border border-red-500/30 flex items-center justify-center mb-4">
                <span className="text-3xl font-black bg-gradient-to-br from-red-400 to-orange-400 bg-clip-text text-transparent">403</span>
              </div>
              <h3 className="text-white font-bold mb-2 text-lg">403 Access Denied</h3>
              <p className="text-gray-500 text-xs mb-3 leading-relaxed">
                3D security shield, terminal animation, glitch effects, dual-mode (full-screen & in-layout)
              </p>
              <div className="flex items-center gap-2 text-red-400 text-sm font-medium">
                View Demo
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </div>
            </Link>
          </div>
        </div>

        {/* Loading Screen */}
        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-6 hover:border-white/[0.12] transition-all opacity-0 animate-fadeIn" style={{ animationDelay: '500ms' }}>
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <Brain className="w-6 h-6 text-teal-400" />
            Loading Screens (8 Unique 3D Variants!)
          </h2>
          <p className="text-gray-400 text-sm mb-6">
            Each has completely different 3D structures, animations, and themed messages. NO repetitive spinners!
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <button
              onClick={() => handleShowLoading('helix')}
              className="group p-4 bg-[#0d0d12] border border-teal-500/20 rounded-xl hover:border-teal-500/40 hover:bg-teal-500/5 transition-all hover:scale-105"
            >
              <div className="w-10 h-10 rounded-lg bg-teal-500/10 border border-teal-500/20 flex items-center justify-center mx-auto mb-2">
                <div className="w-5 h-5 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
              </div>
              <h3 className="text-white font-semibold text-xs mb-0.5">DNA Helix</h3>
              <p className="text-gray-600 text-[10px]">Genetics</p>
            </button>

            <button
              onClick={() => handleShowLoading('orbs')}
              className="group p-4 bg-[#0d0d12] border border-purple-500/20 rounded-xl hover:border-purple-500/40 hover:bg-purple-500/5 transition-all hover:scale-105"
            >
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mx-auto mb-2">
                <div className="flex gap-0.5">
                  <div className="w-1 h-1 rounded-full bg-purple-400 animate-bounce" />
                  <div className="w-1 h-1 rounded-full bg-pink-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-1 h-1 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
              <h3 className="text-white font-semibold text-xs mb-0.5">Energy Orbs</h3>
              <p className="text-gray-600 text-[10px]">General</p>
            </button>

            <button
              onClick={() => handleShowLoading('cyber')}
              className="group p-4 bg-[#0d0d12] border border-red-500/20 rounded-xl hover:border-red-500/40 hover:bg-red-500/5 transition-all hover:scale-105"
            >
              <div className="w-10 h-10 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-2">
                <div className="w-5 h-5 border border-red-400 rounded animate-spin" />
              </div>
              <h3 className="text-white font-semibold text-xs mb-0.5">Cyber Security</h3>
              <p className="text-gray-600 text-[10px]">Shield</p>
            </button>

            <button
              onClick={() => handleShowLoading('education')}
              className="group p-4 bg-[#0d0d12] border border-blue-500/20 rounded-xl hover:border-blue-500/40 hover:bg-blue-500/5 transition-all hover:scale-105"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mx-auto mb-2">
                <div className="w-4 h-5 border border-blue-400 border-b-0" />
              </div>
              <h3 className="text-white font-semibold text-xs mb-0.5">Education/LMS</h3>
              <p className="text-gray-600 text-[10px]">Books</p>
            </button>

            <button
              onClick={() => handleShowLoading('neural')}
              className="group p-4 bg-[#0d0d12] border border-purple-500/20 rounded-xl hover:border-purple-500/40 hover:bg-purple-500/5 transition-all hover:scale-105"
            >
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mx-auto mb-2">
                <div className="grid grid-cols-2 gap-0.5">
                  <div className="w-1 h-1 rounded-full bg-purple-400 animate-pulse" />
                  <div className="w-1 h-1 rounded-full bg-teal-400 animate-pulse" style={{ animationDelay: '200ms' }} />
                  <div className="w-1 h-1 rounded-full bg-blue-400 animate-pulse" style={{ animationDelay: '400ms' }} />
                  <div className="w-1 h-1 rounded-full bg-purple-400 animate-pulse" style={{ animationDelay: '600ms' }} />
                </div>
              </div>
              <h3 className="text-white font-semibold text-xs mb-0.5">AI/ML Neural</h3>
              <p className="text-gray-600 text-[10px]">Network</p>
            </button>

            <button
              onClick={() => handleShowLoading('molecular')}
              className="group p-4 bg-[#0d0d12] border border-emerald-500/20 rounded-xl hover:border-emerald-500/40 hover:bg-emerald-500/5 transition-all hover:scale-105"
            >
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-2">
                <div className="relative w-6 h-6">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-emerald-400" />
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-blue-400" />
                  <div className="absolute bottom-0 left-0 w-1.5 h-1.5 rounded-full bg-amber-400" />
                  <div className="absolute bottom-0 right-0 w-1.5 h-1.5 rounded-full bg-red-400" />
                </div>
              </div>
              <h3 className="text-white font-semibold text-xs mb-0.5">Lab/Chemistry</h3>
              <p className="text-gray-600 text-[10px]">Molecule</p>
            </button>

            <button
              onClick={() => handleShowLoading('quantum')}
              className="group p-4 bg-[#0d0d12] border border-purple-500/20 rounded-xl hover:border-purple-500/40 hover:bg-purple-500/5 transition-all hover:scale-105"
            >
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mx-auto mb-2">
                <div className="w-5 h-5 border-2 border-purple-400 rounded-full animate-ping" />
              </div>
              <h3 className="text-white font-semibold text-xs mb-0.5">Quantum</h3>
              <p className="text-gray-600 text-[10px]">Qubits</p>
            </button>

            <button
              onClick={() => handleShowLoading('database')}
              className="group p-4 bg-[#0d0d12] border border-cyan-500/20 rounded-xl hover:border-cyan-500/40 hover:bg-cyan-500/5 transition-all hover:scale-105"
            >
              <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mx-auto mb-2">
                <Database className="w-5 h-5 text-cyan-400" />
              </div>
              <h3 className="text-white font-semibold text-xs mb-0.5">Database</h3>
              <p className="text-gray-600 text-[10px]">Stacked layers</p>
            </button>

            <button
              onClick={() => handleShowLoading('matrix')}
              className="group p-4 bg-[#0d0d12] border border-teal-500/20 rounded-xl hover:border-teal-500/40 hover:bg-teal-500/5 transition-all hover:scale-105"
            >
              <div className="w-10 h-10 rounded-lg bg-teal-500/10 border border-teal-500/20 flex items-center justify-center mx-auto mb-2">
                <Code className="w-5 h-5 text-teal-400" />
              </div>
              <h3 className="text-white font-semibold text-xs mb-0.5">Matrix Rain</h3>
              <p className="text-gray-600 text-[10px]">Falling code</p>
            </button>
          </div>
        </div>

        {/* Progress Bars */}
        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-6 hover:border-white/[0.12] transition-all opacity-0 animate-fadeIn" style={{ animationDelay: '550ms' }}>
          <h2 className="text-2xl font-bold text-white mb-4">Progress Indicators</h2>
          <p className="text-gray-400 text-sm mb-6">
            Linear bars, circular progress, step indicators with shimmer animations
          </p>

          <div className="space-y-6">
            {/* Linear Progress Bars */}
            <div>
              <h3 className="text-teal-400 font-bold mb-3 text-sm">Linear Progress (4 variants)</h3>
              <div className="space-y-4">
                <ProgressBar value={75} variant="gradient" showLabel showPercentage color="teal" />
                <ProgressBar value={60} variant="striped" showLabel showPercentage color="emerald" />
                <ProgressBar value={85} variant="glow" showLabel showPercentage color="purple" />
                <ProgressBar value={45} variant="default" showLabel showPercentage color="amber" />
              </div>
            </div>

            {/* Circular Progress */}
            <div>
              <h3 className="text-cyan-400 font-bold mb-3 text-sm">Circular Progress</h3>
              <div className="flex flex-wrap gap-6">
                <CircularProgress value={75} color="teal" showPercentage />
                <CircularProgress value={100} color="emerald" showIcon />
                <CircularProgress value={50} color="purple" size={64} strokeWidth={6} />
                <CircularProgress value={30} color="amber" size={96} strokeWidth={10} />
              </div>
            </div>

            {/* Step Progress */}
            <div>
              <h3 className="text-purple-400 font-bold mb-3 text-sm">Step Progress</h3>
              <StepProgress
                steps={[
                  { label: 'Create', description: 'Basic info' },
                  { label: 'Configure', description: 'Add questions' },
                  { label: 'Review', description: 'Check details' },
                  { label: 'Publish', description: 'Go live' }
                ]}
                currentStep={1}
              />
            </div>
          </div>
        </div>

        {/* Alerts */}
        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-6 hover:border-white/[0.12] transition-all opacity-0 animate-fadeIn" style={{ animationDelay: '600ms' }}>
          <h2 className="text-2xl font-bold text-white mb-4">Alert Components</h2>
          <p className="text-gray-400 text-sm mb-6">
            Inline notifications with 4 variants and action buttons
          </p>

          <div className="space-y-4">
            <Alert type="success" title="Success!" variant="default">
              Your exam has been published successfully. Students can now access it.
            </Alert>

            <Alert type="error" title="Error occurred" variant="outlined" dismissible onDismiss={() => {}}>
              Failed to save changes. Please check your connection and try again.
            </Alert>

            <Alert
              type="warning"
              title="Action required"
              variant="filled"
              action={{ label: 'Review Now', onClick: () => alert('Reviewing...') }}
            >
              5 students have not submitted their exams yet. Deadline is approaching.
            </Alert>

            <Alert type="info" variant="minimal">
              New features are available! Check out the updated analytics dashboard.
            </Alert>
          </div>
        </div>

        {/* Empty States - ALL 7 VARIANTS! */}
        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-6 hover:border-white/[0.12] transition-all opacity-0 animate-fadeIn" style={{ animationDelay: '650ms' }}>
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <FileQuestion className="w-6 h-6 text-teal-400" />
            Empty States (7 variants with unique 3D animations!)
          </h2>
          <p className="text-gray-400 text-sm mb-6">
            Each variant has completely different 3D structures: Empty box, Spiral galaxy, Broken chains, Data viz, Empty folder, and more!
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {/* 3D Variant */}
            <div className="bg-[#0d0d12] border border-white/[0.06] rounded-xl hover:border-teal-500/30 transition-all">
              <EmptyState
                icon={Inbox}
                title="No exams found"
                description="Create your first exam to get started"
                actionLabel="Create Exam"
                onAction={() => alert('Creating exam...')}
                size="md"
                variant="3d"
              />
            </div>

            {/* Search Variant */}
            <div className="bg-[#0d0d12] border border-white/[0.06] rounded-xl hover:border-purple-500/30 transition-all">
              <EmptyState
                title="No search results"
                description="Try different keywords or filters"
                actionLabel="Clear Filters"
                secondaryActionLabel="Advanced Search"
                onAction={() => alert('Clearing...')}
                onSecondaryAction={() => alert('Opening...')}
                size="md"
                variant="search"
              />
            </div>

            {/* Locked Variant */}
            <div className="bg-[#0d0d12] border border-white/[0.06] rounded-xl hover:border-red-500/30 transition-all">
              <EmptyState
                title="Access Restricted"
                description="This content is currently locked"
                size="md"
                variant="locked"
              />
            </div>

            {/* Data Variant */}
            <div className="bg-[#0d0d12] border border-white/[0.06] rounded-xl hover:border-cyan-500/30 transition-all">
              <EmptyState
                title="No data available"
                description="Start collecting data to see analytics"
                actionLabel="Import Data"
                onAction={() => alert('Importing...')}
                size="md"
                variant="data"
              />
            </div>

            {/* Folder Variant */}
            <div className="bg-[#0d0d12] border border-white/[0.06] rounded-xl hover:border-blue-500/30 transition-all">
              <EmptyState
                title="No files uploaded"
                description="Drag and drop files to get started"
                actionLabel="Upload Files"
                onAction={() => alert('Opening file picker...')}
                size="md"
                variant="folder"
              />
            </div>

            {/* Default Variant */}
            <div className="bg-[#0d0d12] border border-white/[0.06] rounded-xl hover:border-emerald-500/30 transition-all">
              <EmptyState
                icon={Inbox}
                title="Empty inbox"
                description="You're all caught up!"
                size="md"
                variant="default"
              />
            </div>

            {/* Minimal Variant */}
            <div className="bg-[#0d0d12] border border-white/[0.06] rounded-xl hover:border-gray-500/30 transition-all">
              <EmptyState
                icon={Brain}
                title="Nothing here yet"
                description="Minimal variant for subtle empty states"
                size="md"
                variant="minimal"
              />
            </div>
          </div>
        </div>

        {/* Skeleton Loaders */}
        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-6 hover:border-white/[0.12] transition-all opacity-0 animate-fadeIn" style={{ animationDelay: '700ms' }}>
          <h2 className="text-2xl font-bold text-white mb-4">Skeleton Loaders</h2>
          <p className="text-gray-400 text-sm mb-6">
            Wave shimmer animation for loading states
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-gray-400 text-sm mb-3 font-semibold">Card Skeleton</h3>
              <SkeletonCard />
            </div>

            <div>
              <h3 className="text-gray-400 text-sm mb-3 font-semibold">List Skeleton</h3>
              <SkeletonList items={3} />
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-6 hover:border-white/[0.12] transition-all opacity-0 animate-fadeIn" style={{ animationDelay: '750ms' }}>
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <MousePointer className="w-6 h-6 text-teal-400" />
            Buttons (7 variants with gradient effects!)
          </h2>
          <p className="text-gray-400 text-sm mb-6">
            Interactive buttons with hover effects, loading states, and icon support
          </p>

          <div className="space-y-8">
            {/* All Variants - Medium Size */}
            <div>
              <h3 className="text-gray-400 text-sm mb-4 font-semibold">All Variants</h3>
              <div className="flex flex-wrap gap-3">
                <Button variant="primary">Primary</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="success">Success</Button>
                <Button variant="danger">Danger</Button>
                <Button variant="warning">Warning</Button>
                <Button variant="outline">Outline</Button>
                <Button variant="ghost">Ghost</Button>
              </div>
            </div>

            {/* Sizes */}
            <div>
              <h3 className="text-gray-400 text-sm mb-4 font-semibold">Sizes</h3>
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="primary" size="sm">Small</Button>
                <Button variant="primary" size="md">Medium</Button>
                <Button variant="primary" size="lg">Large</Button>
              </div>
            </div>

            {/* With Icons */}
            <div>
              <h3 className="text-gray-400 text-sm mb-4 font-semibold">With Icons</h3>
              <div className="flex flex-wrap gap-3">
                <Button variant="primary" leftIcon={<Download className="w-4 h-4" />}>
                  Download
                </Button>
                <Button variant="success" leftIcon={<CheckCircle className="w-4 h-4" />}>
                  Approve
                </Button>
                <Button variant="danger" leftIcon={<Trash2 className="w-4 h-4" />}>
                  Delete
                </Button>
                <Button variant="secondary" rightIcon={<Mail className="w-4 h-4" />}>
                  Send Email
                </Button>
              </div>
            </div>

            {/* Loading States */}
            <div>
              <h3 className="text-gray-400 text-sm mb-4 font-semibold">Loading States</h3>
              <div className="flex flex-wrap gap-3">
                <Button variant="primary" isLoading>Loading...</Button>
                <Button variant="secondary" isLoading>Processing...</Button>
                <Button variant="success" isLoading>Saving...</Button>
              </div>
            </div>
          </div>
        </div>

        {/* Badges */}
        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-6 hover:border-white/[0.12] transition-all opacity-0 animate-fadeIn" style={{ animationDelay: '800ms' }}>
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <Star className="w-6 h-6 text-teal-400" />
            Badges (9 color variants!)
          </h2>
          <p className="text-gray-400 text-sm mb-6">
            Status indicators with optional dot and multiple sizes
          </p>

          <div className="space-y-8">
            {/* All Variants */}
            <div>
              <h3 className="text-gray-400 text-sm mb-4 font-semibold">All Variants</h3>
              <div className="flex flex-wrap gap-3">
                <Badge variant="primary">Primary</Badge>
                <Badge variant="secondary">Secondary</Badge>
                <Badge variant="success">Success</Badge>
                <Badge variant="danger">Danger</Badge>
                <Badge variant="warning">Warning</Badge>
                <Badge variant="info">Info</Badge>
                <Badge variant="teal">Teal</Badge>
                <Badge variant="purple">Purple</Badge>
                <Badge variant="cyan">Cyan</Badge>
              </div>
            </div>

            {/* With Dots */}
            <div>
              <h3 className="text-gray-400 text-sm mb-4 font-semibold">With Status Dots</h3>
              <div className="flex flex-wrap gap-3">
                <Badge variant="success" dot>Active</Badge>
                <Badge variant="warning" dot>Pending</Badge>
                <Badge variant="danger" dot>Offline</Badge>
                <Badge variant="info" dot>In Progress</Badge>
                <Badge variant="teal" dot>Published</Badge>
              </div>
            </div>

            {/* Sizes */}
            <div>
              <h3 className="text-gray-400 text-sm mb-4 font-semibold">Sizes</h3>
              <div className="flex flex-wrap items-center gap-3">
                <Badge variant="primary" size="sm">Small</Badge>
                <Badge variant="primary" size="md">Medium</Badge>
                <Badge variant="primary" size="lg">Large</Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Cards */}
        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-6 hover:border-white/[0.12] transition-all opacity-0 animate-fadeIn" style={{ animationDelay: '850ms' }}>
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <Layers className="w-6 h-6 text-teal-400" />
            Cards (Multiple variants with accent colors!)
          </h2>
          <p className="text-gray-400 text-sm mb-6">
            Flexible card containers with headers, footers, and gradient accents
          </p>

          <div className="space-y-8">
            {/* Default Card */}
            <div>
              <h3 className="text-gray-400 text-sm mb-4 font-semibold">Default Card</h3>
              <Card>
                <CardHeader
                  icon={<Package className="w-4 h-4 text-teal-400" />}
                  title="Default Card"
                  subtitle="Basic card with no accent"
                />
                <p className="text-white/70 text-sm">
                  This is a default card with standard styling. Perfect for general content display.
                </p>
                <CardFooter>
                  <Button variant="primary" size="sm">Learn More</Button>
                </CardFooter>
              </Card>
            </div>

            {/* Cards with Accent Colors */}
            <div>
              <h3 className="text-gray-400 text-sm mb-4 font-semibold">With Accent Colors</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Card accent="teal" hover>
                  <CardHeader
                    icon={<CheckCircle className="w-4 h-4 text-teal-400" />}
                    title="Teal Accent"
                    subtitle="Success theme"
                  />
                  <p className="text-white/70 text-sm">
                    Card with teal gradient accent
                  </p>
                </Card>

                <Card accent="purple" hover>
                  <CardHeader
                    icon={<Star className="w-4 h-4 text-purple-400" />}
                    title="Purple Accent"
                    subtitle="Premium theme"
                  />
                  <p className="text-white/70 text-sm">
                    Card with purple gradient accent
                  </p>
                </Card>

                <Card accent="blue" hover>
                  <CardHeader
                    icon={<Info className="w-4 h-4 text-blue-400" />}
                    title="Blue Accent"
                    subtitle="Info theme"
                  />
                  <p className="text-white/70 text-sm">
                    Card with blue gradient accent
                  </p>
                </Card>

                <Card accent="emerald" hover>
                  <CardHeader
                    icon={<Zap className="w-4 h-4 text-emerald-400" />}
                    title="Emerald Accent"
                    subtitle="Success theme"
                  />
                  <p className="text-white/70 text-sm">
                    Card with emerald gradient accent
                  </p>
                </Card>

                <Card accent="amber" hover>
                  <CardHeader
                    icon={<AlertCircle className="w-4 h-4 text-amber-400" />}
                    title="Amber Accent"
                    subtitle="Warning theme"
                  />
                  <p className="text-white/70 text-sm">
                    Card with amber gradient accent
                  </p>
                </Card>

                <Card accent="red" hover>
                  <CardHeader
                    icon={<AlertCircle className="w-4 h-4 text-red-400" />}
                    title="Red Accent"
                    subtitle="Danger theme"
                  />
                  <p className="text-white/70 text-sm">
                    Card with red gradient accent
                  </p>
                </Card>
              </div>
            </div>

            {/* Glass Card */}
            <div>
              <h3 className="text-gray-400 text-sm mb-4 font-semibold">Glass Variant</h3>
              <Card variant="glass" accent="teal" hover>
                <CardHeader
                  icon={<Sparkles className="w-4 h-4 text-teal-400" />}
                  title="Glass Card"
                  subtitle="Enhanced backdrop blur"
                />
                <p className="text-white/70 text-sm">
                  Card with enhanced glass morphism effect for a more modern look.
                </p>
                <CardFooter>
                  <div className="flex gap-2">
                    <Button variant="primary" size="sm">Action 1</Button>
                    <Button variant="ghost" size="sm">Action 2</Button>
                  </div>
                </CardFooter>
              </Card>
            </div>
          </div>
        </div>

        {/* Inputs */}
        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-6 hover:border-white/[0.12] transition-all opacity-0 animate-fadeIn" style={{ animationDelay: '900ms' }}>
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-teal-400" />
            Input Fields (With icons, validation, and states!)
          </h2>
          <p className="text-gray-400 text-sm mb-6">
            Form inputs with labels, icons, error states, and helper text
          </p>

          <div className="space-y-8 max-w-2xl">
            {/* Basic Input */}
            <div>
              <h3 className="text-gray-400 text-sm mb-4 font-semibold">Basic Input</h3>
              <Input
                label="Email Address"
                type="email"
                placeholder="Enter your email"
                helperText="We'll never share your email with anyone else"
              />
            </div>

            {/* Required Input */}
            <div>
              <h3 className="text-gray-400 text-sm mb-4 font-semibold">Required Input</h3>
              <Input
                label="Username"
                type="text"
                placeholder="Choose a username"
                required
              />
            </div>

            {/* With Left Icon */}
            <div>
              <h3 className="text-gray-400 text-sm mb-4 font-semibold">With Left Icon</h3>
              <Input
                label="Search"
                type="text"
                placeholder="Search exams..."
                leftIcon={<Search className="w-4 h-4" />}
              />
            </div>

            {/* With Right Icon */}
            <div>
              <h3 className="text-gray-400 text-sm mb-4 font-semibold">With Right Icon</h3>
              <Input
                label="Full Name"
                type="text"
                placeholder="John Doe"
                rightIcon={<User className="w-4 h-4" />}
              />
            </div>

            {/* Error State */}
            <div>
              <h3 className="text-gray-400 text-sm mb-4 font-semibold">Error State</h3>
              <Input
                label="Password"
                type="password"
                placeholder="Enter password"
                error="Password must be at least 8 characters long"
                leftIcon={<Eye className="w-4 h-4" />}
              />
            </div>

            {/* Disabled State */}
            <div>
              <h3 className="text-gray-400 text-sm mb-4 font-semibold">Disabled State</h3>
              <Input
                label="Disabled Field"
                type="text"
                placeholder="This field is disabled"
                disabled
                value="Cannot edit this"
              />
            </div>

            {/* Multiple Inputs */}
            <div>
              <h3 className="text-gray-400 text-sm mb-4 font-semibold">Form Example</h3>
              <div className="space-y-4">
                <Input
                  label="Email"
                  type="email"
                  placeholder="student@example.com"
                  leftIcon={<Mail className="w-4 h-4" />}
                  required
                />
                <Input
                  label="Password"
                  type="password"
                  placeholder="Enter your password"
                  leftIcon={<EyeOff className="w-4 h-4" />}
                  helperText="Must be at least 8 characters"
                  required
                />
                <Button variant="primary" className="w-full">Sign In</Button>
              </div>
            </div>
          </div>
        </div>

        {/* Back Link */}
        <div className="text-center pt-6 opacity-0 animate-fadeIn" style={{ animationDelay: '600ms' }}>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white/[0.04] border border-white/[0.06] text-gray-300 rounded-xl hover:bg-white/[0.08] hover:border-white/[0.12] font-medium transition-all hover:scale-105"
          >
            ← Back to Home
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse-slow {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }

        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out forwards;
        }

        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default ComponentShowcase;
